import React, { useState } from 'react';
import { toast } from 'sonner';
import { ClipboardList, Loader2, Pencil } from 'lucide-react';
import { JobInventoryFields } from './JobInventoryFields';
import { inventoryFromJob, inventoryLines, inventoryFacts, jobKind } from './inventoryUtils';
import { JobPhotos } from '../JobPhotos';

export const JobInventoryEditor = ({ job, authFetch, onSaved, showWastePhotos = true }) => {
  const [open, setOpen] = useState(false);
  const [inv, setInv] = useState(() => inventoryFromJob(job));
  const [saving, setSaving] = useState(false);
  const current = inventoryFromJob(job);
  const lines = inventoryLines(current);
  const facts = inventoryFacts(current, job.service);

  const save = async () => {
    setSaving(true);
    try {
      await authFetch('patch', `/admin/estimate/${job.id}/inventory`, inv);
      toast.success('Job items updated.');
      setOpen(false);
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save items."); }
    finally { setSaving(false); }
  };

  return (
    <div className="rounded-md border border-slate-200 bg-white p-3 space-y-2" data-testid={`job-inventory-${job.ref}`}>
      <div className="flex items-center gap-2">
        <ClipboardList className="w-4 h-4 text-[color:var(--mv-orange)]" />
        <div className="flex-1 text-[10px] font-black uppercase tracking-wider text-slate-500">Items &amp; job details</div>
        {!open && <button type="button" onClick={() => { setInv(inventoryFromJob(job)); setOpen(true); }} className="h-9 px-3 rounded-md border border-slate-200 text-xs font-bold text-[color:var(--mv-navy)] inline-flex items-center gap-1.5" data-testid="job-inventory-edit"><Pencil className="w-3.5 h-3.5" /> Edit items</button>}
      </div>
      {!open && (
        <div className="text-xs text-slate-700 space-y-1" data-testid="job-inventory-summary">
          {facts.length > 0 && <div className="font-bold text-[color:var(--mv-navy)]">{facts.join(' · ')}</div>}
          {lines.length ? <ul className="list-disc pl-4 space-y-0.5">{lines.map(l => <li key={l}>{l}</li>)}</ul> : <div className="text-slate-400 italic">No items listed yet — tap Edit items.</div>}
          {job.inventory_edited?.role === 'driver' && <div className="text-[11px] font-bold text-amber-700" data-testid="job-inventory-driver-edited">Updated on the day by {job.inventory_edited.by} · {new Date(job.inventory_edited.at + (job.inventory_edited.at.endsWith('Z') || job.inventory_edited.at.includes('+') ? '' : 'Z')).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' })}</div>}
        </div>
      )}
      {jobKind(job.service) === 'clearance' && showWastePhotos && <JobPhotos job={job} authFetch={authFetch} onUpdated={onSaved} canEdit kind="waste" title="Waste photos" />}
      {open && (
        <>
          <JobInventoryFields service={job.service} value={inv} onChange={setInv} />
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={() => setOpen(false)} className="h-10 px-4 text-sm font-bold text-slate-500" data-testid="job-inventory-cancel">Cancel</button>
            <button type="button" onClick={save} disabled={saving} className="h-10 px-5 rounded-md bg-[color:var(--mv-navy)] text-white text-sm font-bold inline-flex items-center gap-1.5" data-testid="job-inventory-save">{saving && <Loader2 className="w-4 h-4 animate-spin" />} Save items</button>
          </div>
        </>
      )}
    </div>
  );
};
